import React, { useMemo, useCallback, useState } from 'react';
import type { FC } from 'react';
import { join, relative } from 'node:path';
import { isType, match } from 'variant';
import { TextInput } from '@wix/cli-ui-kit';
import { useTranslation } from '@wix/cli-i18n';
import kebabCase from 'lodash/kebabCase.js';
import { RepoType } from '@wix/package-manager';
import {
  validateDirectory,
  validatePackageName,
} from '../validations/index.js';
import { useCreateAppBi } from '../bi/useCreateAppBi.js';
import { ConfirmPackagePath } from './ConfirmPackagePath.js';

interface ChoosePackageNameProps {
  appName: string;
  targetParentFolder: string;
  repoType: RepoType;
  onSelected: (packageName: string) => void;
}

export const ChoosePackageName: FC<ChoosePackageNameProps> = ({
  appName,
  targetParentFolder,
  repoType,
  onSelected,
}) => {
  const { t } = useTranslation();
  const { cliFlowStepAnswered } = useCreateAppBi();

  const [packageName, setPackageName] = useState<string>();

  const defaultPackageName = useMemo(() => {
    return kebabCase(appName);
  }, [appName]);

  const isMonorepo = isType(repoType, RepoType.Monorepo);

  async function validate(packageName: string) {
    if (!(await validateDirectory(join(targetParentFolder, packageName)))) {
      return t('validation_errors.invalid_directory');
    }

    const npmValidationErrors = await validatePackageName(packageName);

    return match(npmValidationErrors, {
      Ok: () => true as const,
      Error: ({ payload }) =>
        t('validation_errors.invalid_package_name', {
          errors: payload.join(', '),
        }),
    });
  }

  const handlePackageNameSet = useCallback(
    (packageName: string) => {
      cliFlowStepAnswered({
        question: t('create_app.choose_package_name.label'),
        questionKey: 'create_app.choose_package_name.label',
        answer: packageName,
      });

      if (isMonorepo) {
        setPackageName(packageName);
      } else {
        onSelected(packageName);
      }
    },
    [cliFlowStepAnswered, setPackageName, onSelected, isMonorepo, t]
  );

  const handlePackagePathConfirm = useCallback(
    (packageName: string, isConfirmed: boolean) => {
      if (isConfirmed) {
        onSelected(packageName);
      }
    },
    [onSelected]
  );

  return (
    <>
      <TextInput
        label={t('create_app.choose_package_name.label')}
        placeholder={defaultPackageName}
        onSubmit={handlePackageNameSet}
        validate={validate}
        inCreateFlow
      />

      {packageName && isMonorepo && (
        <ConfirmPackagePath
          path={relative(
            repoType.rootDir,
            join(targetParentFolder, packageName)
          )}
          onSubmit={(confirmed) =>
            handlePackagePathConfirm(packageName, confirmed)
          }
        />
      )}
    </>
  );
};
